<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { validateCoupons, ValidatedCouponResult } from '@/api/api';
import { useBookingDataStore } from '@/stores/bookingData';
import { useTextStore } from '@/stores/texts';

const props = defineProps<{ totalCents: number }>();
const emit = defineEmits<{ (e: 'update', totals: { discountCents: number; remainingDueCents: number }): void }>();

const bookingData = useBookingDataStore();
const textStore = useTextStore();

const input = ref('');
const loading = ref(false);
const errorText = ref('');
const applied = ref<ValidatedCouponResult[]>([]);

// Texts-store keys with French fallbacks. The rows are seeded at rollout; the
// fallback keeps the UI functional (and never shows a hardcoded literal in the
// template) if a row is missing.
const FALLBACKS: Record<string, string> = {
  coupon_title: 'Bon cadeau ou code promo',
  coupon_placeholder: 'Entrez votre code',
  coupon_add: 'Ajouter',
  coupon_err_unknown_code: 'Code inconnu',
  coupon_err_expired: 'Ce code est expiré',
  coupon_err_not_yet_valid: "Ce code n'est pas encore valable",
  coupon_err_depleted: 'Ce bon est épuisé',
  coupon_err_voided: 'Ce code a été annulé',
  coupon_err_disabled: 'Ce code est désactivé',
  coupon_err_duplicate_code: 'Code déjà ajouté',
  coupon_err_promo_already_applied: 'Un seul code promo par réservation',
  coupon_err_below_min_booking: 'Montant minimum de réservation non atteint',
  coupon_err_max_redemptions_reached: "Ce code a atteint son nombre maximum d'utilisations",
  coupon_err_giftup_unavailable: 'Vérification du bon impossible pour le moment, veuillez réessayer',
  coupon_err_not_applicable: 'Ce code ne peut pas être appliqué',
};
const labels = ref<Record<string, string>>({ ...FALLBACKS });
onMounted(async () => {
  for (const key of Object.keys(FALLBACKS)) {
    labels.value[key] = (await textStore.getText(key)) || FALLBACKS[key];
  }
});

function euros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}
function chipLabel(r: ValidatedCouponResult): string {
  return `${r.code} −${euros(r.amountAppliedCents ?? 0)}`;
}

// Resolves true when validation succeeded and `addedCode` (if any) was accepted.
async function revalidate(codes: string[], addedCode?: string): Promise<boolean> {
  loading.value = true;
  errorText.value = '';
  try {
    const res = await validateCoupons(codes, props.totalCents);
    if (!res.success || !res.data) {
      errorText.value = labels.value.coupon_err_giftup_unavailable;
      return false;
    }
    const ok = res.data.results.filter(r => r.status === 'ok');
    const addedResult = addedCode ? res.data.results.find(r => r.code === addedCode.trim().toUpperCase()) : undefined;
    if (addedResult && addedResult.status !== 'ok') {
      errorText.value = labels.value[`coupon_err_${addedResult.status}`] ?? labels.value.coupon_err_not_applicable;
    } else if (addedCode) {
      input.value = '';
    }
    applied.value = ok;
    bookingData.setCouponCodes(ok.map(r => r.code));
    emit('update', { discountCents: res.data.discountCents, remainingDueCents: res.data.remainingDueCents });
    return !addedResult || addedResult.status === 'ok';
  } finally {
    loading.value = false;
  }
}

async function addCode(): Promise<boolean> {
  // Codes are stored/compared upper-cased — the backend echoes them upper-cased
  // in `results`, so normalise on entry to keep add / dedupe / remove aligned.
  const code = input.value.trim().toUpperCase();
  if (!code) return true;
  return revalidate([...applied.value.map(r => r.code), code], code);
}

// Called by the summary before paying: a code typed but never "Ajouter"-ed was
// otherwise silently dropped and the customer charged full price. Resolves true
// when payment may proceed (nothing pending, or the pending code applied); false
// leaves the rejection message on screen and payment must stop.
function applyPending(): Promise<boolean> {
  return addCode();
}
defineExpose({ applyPending });

async function removeCode(code: string): Promise<void> {
  await revalidate(applied.value.map(r => r.code).filter(c => c !== code));
}

// Bidirectional sync with the persisted store. Immediate so a reload/back-nav
// rebuilds the chips from the persisted codes (hydration); reactive so an
// external clear (e.g. the summary's 422 `coupon_invalid` branch calling
// setCouponCodes([])) empties the widget and resets the total. `revalidate`
// itself writes the ok codes back via setCouponCodes, which re-triggers this
// watcher — the equal-arrays guard breaks the loop.
watch(() => bookingData.appliedCouponCodes.join(' '), () => {
  const storeCodes = bookingData.appliedCouponCodes;
  const localCodes = applied.value.map(r => r.code);
  if (storeCodes.join(' ') === localCodes.join(' ')) return;
  if (storeCodes.length === 0) {
    applied.value = [];
    errorText.value = '';
    emit('update', { discountCents: 0, remainingDueCents: props.totalCents });
    return;
  }
  void revalidate([...storeCodes]);
}, { immediate: true });
</script>

<template>
  <div class="coupon-codes">
    <h3>{{ labels.coupon_title }}</h3>
    <div class="coupon-codes__row">
      <v-text-field
        v-model="input"
        data-testid="coupon-input"
        :placeholder="labels.coupon_placeholder"
        density="compact"
        hide-details
        :disabled="loading"
        @keyup.enter="addCode"
      />
      <v-btn data-testid="coupon-add" :loading="loading" @click="addCode">{{ labels.coupon_add }}</v-btn>
    </div>
    <p v-if="errorText" data-testid="coupon-error" class="coupon-codes__error">{{ errorText }}</p>
    <div v-if="applied.length" class="coupon-codes__chips">
      <v-chip
        v-for="r in applied"
        :key="r.code"
        :data-testid="`coupon-chip-${r.code}`"
        closable
        @click:close="removeCode(r.code)"
      >
        {{ chipLabel(r) }}
      </v-chip>
    </div>
  </div>
</template>

<style scoped>
.coupon-codes {
  margin-top: 16px;
}
.coupon-codes__row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.coupon-codes__error {
  color: #b00020;
  margin: 4px 0 0;
  font-size: 0.875rem;
}
.coupon-codes__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
</style>
